import { Container, Graphics, Text } from 'pixi.js';
import type { Snapshot } from '@nebulark/engine';
import { palette, WORLD_W, WORLD_H } from './theme';

/**
 * HUD overlay drawn in logical-world coordinates above the shaken game layer.
 * Reconciles text + graphics from snapshots each frame.
 */
export class Hud {
  readonly container: Container;

  private readonly scoreText: Text;
  private readonly hiScoreText: Text;
  private readonly weaponText: Text;
  private readonly livesContainer: Container;
  private readonly bombButton: Graphics;
  private readonly bombLabel: Text;
  private readonly bombCountText: Text;
  private readonly bossBarBg: Graphics;
  private readonly bossBarFill: Graphics;

  private readonly bombButtonX: number;
  private readonly bombButtonY: number;
  private readonly bombButtonR: number;

  constructor() {
    this.container = new Container();
    this.container.sortableChildren = false;

    const baseStyle = {
      fill: palette.hud,
      fontFamily: 'Menlo, Monaco, Consolas, monospace',
      fontSize: 14,
    } as const;

    // Top-left: score + hi-score
    this.scoreText = new Text({ text: 'SCORE 0', style: baseStyle });
    this.scoreText.x = 8;
    this.scoreText.y = 8;
    this.container.addChild(this.scoreText);

    this.hiScoreText = new Text({ text: 'HI 0', style: baseStyle });
    this.hiScoreText.x = 8;
    this.hiScoreText.y = 24;
    this.container.addChild(this.hiScoreText);

    // Top-right: lives
    this.livesContainer = new Container();
    this.livesContainer.x = WORLD_W - 8;
    this.livesContainer.y = 14;
    this.container.addChild(this.livesContainer);

    // Bottom-left: weapon name + level pips
    this.weaponText = new Text({ text: 'SPREAD 1', style: baseStyle });
    this.weaponText.x = 8;
    this.weaponText.y = WORLD_H - 22;
    this.container.addChild(this.weaponText);

    // Bottom-right: bomb button
    this.bombButtonR = 26;
    this.bombButtonX = WORLD_W - this.bombButtonR - 14;
    this.bombButtonY = WORLD_H - this.bombButtonR - 14;

    this.bombButton = new Graphics();
    this.bombButton.circle(0, 0, this.bombButtonR).fill({ color: 0x222a44, alpha: 0.6 }).stroke({ color: palette.hud, width: 2 });
    this.bombButton.x = this.bombButtonX;
    this.bombButton.y = this.bombButtonY;
    this.container.addChild(this.bombButton);

    this.bombLabel = new Text({
      text: 'B',
      style: { ...baseStyle, fontSize: 22, fill: palette.hud },
    });
    this.bombLabel.anchor.set(0.5);
    this.bombLabel.x = this.bombButtonX;
    this.bombLabel.y = this.bombButtonY - 2;
    this.container.addChild(this.bombLabel);

    this.bombCountText = new Text({ text: 'x0', style: baseStyle });
    this.bombCountText.anchor.set(0.5, 0);
    this.bombCountText.x = this.bombButtonX;
    this.bombCountText.y = this.bombButtonY + this.bombButtonR - 6;
    this.container.addChild(this.bombCountText);

    // Top-center: boss HP bar (hidden until boss active)
    this.bossBarBg = new Graphics();
    this.bossBarBg.rect(-80, 0, 160, 6).fill({ color: 0x222a44, alpha: 0.7 });
    this.bossBarBg.x = WORLD_W / 2;
    this.bossBarBg.y = 48;
    this.bossBarBg.visible = false;
    this.container.addChild(this.bossBarBg);

    this.bossBarFill = new Graphics();
    this.bossBarFill.rect(-80, 0, 160, 6).fill(palette.boss);
    this.bossBarFill.x = WORLD_W / 2;
    this.bossBarFill.y = 48;
    this.bossBarFill.visible = false;
    this.container.addChild(this.bossBarFill);
  }

  update(snapshot: Snapshot): void {
    const p = snapshot.players[0];

    const score = p?.score ?? 0;
    if (this.scoreText.text !== `SCORE ${score}`) {
      this.scoreText.text = `SCORE ${score}`;
    }

    const hi = snapshot.hiScore ?? 0;
    if (this.hiScoreText.text !== `HI ${hi}`) {
      this.hiScoreText.text = `HI ${hi}`;
    }

    // Lives row: draw N small triangles from right to left
    const lives = Math.max(0, p?.lives ?? 0);
    while (this.livesContainer.children.length > lives) {
      const child = this.livesContainer.children[this.livesContainer.children.length - 1]!;
      this.livesContainer.removeChild(child);
      child.destroy();
    }
    while (this.livesContainer.children.length < lives) {
      const g = new Graphics();
      g.moveTo(0, -6).lineTo(5, 5).lineTo(-5, 5).lineTo(0, -6).fill(palette.playerFill);
      this.livesContainer.addChild(g);
    }
    for (let i = 0; i < this.livesContainer.children.length; i++) {
      const c = this.livesContainer.children[i]!;
      c.x = -i * 14;
      c.y = 0;
    }

    // Weapon name + level pips
    const weapon = p?.weapon;
    if (weapon) {
      const pipTotal = 3;
      let pips = '';
      for (let i = 0; i < pipTotal; i++) pips += i < weapon.level ? 'o' : '.';
      const label = `${weapon.kind.toUpperCase()} ${pips}`;
      if (this.weaponText.text !== label) this.weaponText.text = label;
    }

    // Bomb count
    const bombs = p?.bombs ?? 0;
    const bombLabel = `x${bombs}`;
    if (this.bombCountText.text !== bombLabel) this.bombCountText.text = bombLabel;
    this.bombButton.alpha = bombs > 0 ? 1 : 0.4;

    // Boss HP bar
    const boss = snapshot.boss;
    if (boss && boss.hpMax > 0) {
      this.bossBarBg.visible = true;
      this.bossBarFill.visible = true;
      const frac = Math.max(0, Math.min(1, boss.hp / boss.hpMax));
      this.bossBarFill.scale.x = frac;
    } else {
      this.bossBarBg.visible = false;
      this.bossBarFill.visible = false;
    }
  }

  /**
   * Returns the bomb button hitbox in logical (world) coordinates.
   * Callers that receive screen coords must convert via the screen transform
   * before hit-testing.
   */
  getBombButtonHitbox(): { x: number; y: number; radius: number } {
    return { x: this.bombButtonX, y: this.bombButtonY, radius: this.bombButtonR };
  }
}
